/* =========================================================================
   FRÖKEN SNUSK — global.js
   Minimal vanilla JS. No frameworks, no dependencies.
   Progressive enhancement: the theme is fully usable without this file.
   ========================================================================= */
(function () {
  'use strict';

  var root = document.documentElement;

  /* ---- Sticky header state ---------------------------------------------- */
  var header = document.querySelector('[data-header]');
  if (header) {
    var onScroll = function () {
      root.classList.toggle('is-scrolled', window.scrollY > 8);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---- Mobile navigation toggle ----------------------------------------- */
  /* Two buttons control the same panel (the header burger and the overlay's close
     button). State must be derived from the PANEL — if each button reads its own
     aria-expanded they desync after the first close and the burger stops opening.
     Every toggle pointing at the panel is kept in sync. */
  var navLastFocus = null;

  function focusable(root) {
    return Array.prototype.slice.call(root.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(function (el) { return el.offsetParent !== null || el === document.activeElement; });
  }

  function setNavOpen(panel, targetId, open) {
    panel.hidden = !open;
    document.body.classList.toggle('nav-open', open);
    document
      .querySelectorAll('[data-nav-toggle][aria-controls="' + targetId + '"]')
      .forEach(function (t) { t.setAttribute('aria-expanded', String(open)); });

    /* Without this the panel opened and focus stayed behind it: the logo, search and
       cart are still in the tab order underneath the overlay, so a keyboard user
       tabbed through three invisible controls before reaching the menu — and on close
       focus fell to <body> and they started over from the top of the document. */
    if (open) {
      navLastFocus = document.activeElement;
      var first = focusable(panel)[0];
      if (first) first.focus();
    } else if (navLastFocus && document.contains(navLastFocus)) {
      navLastFocus.focus();
      navLastFocus = null;
    }
  }

  document.querySelectorAll('[data-nav-toggle]').forEach(function (btn) {
    var targetId = btn.getAttribute('aria-controls');
    var panel = targetId && document.getElementById(targetId);
    if (!panel) return;
    btn.addEventListener('click', function () {
      setNavOpen(panel, targetId, panel.hidden);
    });

    panel.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { setNavOpen(panel, targetId, false); return; }
      if (e.key !== 'Tab') return;
      /* Recomputed on every Tab rather than cached — the panel's contents can change
         (submenus expand) and a stale list would let focus escape. */
      var items = focusable(panel);
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  });

  /* ---- Hero video is desktop-only when a mobile image exists -------------
     CSS alone only hides it; the element can still fetch. Removing it stops the
     download outright. Only runs when Liquid confirmed a mobile fallback exists. */
  var heroVideo = document.querySelector('[data-hero-video="has-mobile-fallback"]');
  if (heroVideo && window.matchMedia('(max-width: 749px)').matches) {
    try { heroVideo.pause(); } catch (e) {}
    heroVideo.removeAttribute('autoplay');
    heroVideo.remove();
  }

  /* ---- Hero video pause control (WCAG 2.2.2, level A) --------------------
     Anything that moves automatically for more than five seconds must be stoppable.
     The button ships hidden and is revealed here, so it is never a dead control if
     this script fails to load. */
  document.querySelectorAll('[data-video-toggle]').forEach(function (btn) {
    var wrap = btn.parentElement;
    var video = wrap && wrap.querySelector('video');
    if (!video) return;
    var label = btn.querySelector('[data-video-toggle-label]');
    btn.hidden = false;
    btn.setAttribute('aria-pressed', 'false');

    function sync(paused) {
      btn.setAttribute('aria-pressed', String(paused));
      if (label) {
        label.textContent = paused
          ? (btn.dataset.labelPlay || 'Spela videon')
          : (btn.dataset.labelPause || 'Pausa videon');
      }
    }
    btn.addEventListener('click', function () {
      if (video.paused) { video.play(); } else { video.pause(); }
    });
    /* Listen to the element, not the click: autoplay can be blocked, and the browser
       can pause the video on its own when the tab is hidden. */
    video.addEventListener('play', function () { sync(false); });
    video.addEventListener('pause', function () { sync(true); });
    sync(video.paused);
  });

  /* ---- Price range: two handles over two number fields ------------------- */
  document.querySelectorAll('[data-price-range]').forEach(function (root) {
    var wrap = root.querySelector('[data-price-slider-wrap]');
    var minS = root.querySelector('[data-price-slider="min"]');
    var maxS = root.querySelector('[data-price-slider="max"]');
    var minN = root.querySelector('[data-price-min]');
    var maxN = root.querySelector('[data-price-max]');
    var fill = root.querySelector('[data-price-fill]');
    var max = Number(root.getAttribute('data-max')) || 0;
    if (!wrap || !minS || !maxS || !minN || !maxN || !max) return;

    wrap.hidden = false;

    function paint() {
      var lo = Number(minS.value);
      var hi = Number(maxS.value);
      /* Both inputs span the full width, so the one painted later wins every
         overlapping hit test. Only the THUMBS take pointer events, so burial happens
         solely where the two thumbs overlap — but there it is total: at equal values
         the min handle cannot be grabbed at all. Raising min above max in exactly that
         state keeps it reachable, and dragging it left separates them again. Toggled
         here rather than in fromSlider so a page arriving with a server-rendered
         filter, and edits typed into the number fields, get the right stacking too. */
      minS.classList.toggle('is-top', lo >= hi);
      fill.style.left = (lo / max * 100) + '%';
      fill.style.right = (100 - (hi / max * 100)) + '%';
    }

    function fromSlider(which) {
      var lo = Number(minS.value);
      var hi = Number(maxS.value);
      /* CLAMP the handle being dragged — never push the other one. Pushing welded them
         together, and from a welded state the buried handle could not be separated by
         mouse again: dragging left moved both, so the band stayed zero-width and the
         collection stayed empty however far the shopper dragged. Stopping against the
         neighbour is what every native dual-range does, and it is always recoverable. */
      if (lo > hi) {
        if (which === 'min') { lo = hi; minS.value = lo; }
        else { hi = lo; maxS.value = hi; }
      }
      /* Only the field whose handle actually moved is rewritten. Writing both rounded
         away öre the shopper had typed into the field they never touched — the slider
         steps in whole kronor, the number field accepts decimals.
         An empty field means "no bound", so a handle parked at its end clears its own
         field rather than submitting 0 or the maximum as a filter nobody set. */
      if (which === 'min') minN.value = lo <= 0 ? '' : lo;
      else maxN.value = hi >= max ? '' : hi;
      paint();
    }

    function fromNumber() {
      var lo = minN.value === '' ? 0 : Math.max(0, Math.min(max, Number(minN.value)));
      var hi = maxN.value === '' ? max : Math.max(0, Math.min(max, Number(maxN.value)));
      /* An inverted pair typed by hand has to be corrected in the FIELDS as well as on
         the sliders. Correcting only the sliders left the bar showing one range while a
         different, inverted range was what actually submitted — and that returns
         nothing, with the slider apparently showing a perfectly sensible band. */
      if (lo > hi) {
        lo = hi;
        if (minN.value !== '') minN.value = lo;
      }
      minS.value = lo;
      maxS.value = hi;
      paint();
    }

    minS.addEventListener('input', function () { fromSlider('min'); });
    maxS.addEventListener('input', function () { fromSlider('max'); });
    /* change, not input. On input this ran per keystroke, so typing the "1" of "150"
       yanked the other handle across the bar, and a momentarily empty field mid-edit
       was read as "no bound". change fires when the value is committed. */
    minN.addEventListener('change', fromNumber);
    maxN.addEventListener('change', fromNumber);
    paint();
  });

  /* ---- Filter popover positioning ---------------------------------------- */
  /* A CSS :last-child flip was wrong twice over: it flips whichever filter is last in
     the DOM even when that one has wrapped onto the LEFT of a second row — throwing the
     panel off the left edge instead — and it does nothing for a middle chip that
     happens to sit near the right edge on a mid-size window. body has
     overflow-x:hidden, so anything pushed off-screen is unreachable, not merely
     scrolled past. Measuring is the only thing that survives wrapping. */
  function positionFilterPanel(details) {
    var panel = details.querySelector('.collection-filter__body');
    if (!panel || window.innerWidth < 750) return;
    panel.classList.remove('is-flipped');
    var r = panel.getBoundingClientRect();
    if (r.right > window.innerWidth - 8) panel.classList.add('is-flipped');
  }
  document.querySelectorAll('.collection-filter').forEach(function (d) {
    d.addEventListener('toggle', function () { if (d.open) positionFilterPanel(d); });
  });
  window.addEventListener('resize', function () {
    document.querySelectorAll('.collection-filter[open]').forEach(positionFilterPanel);
  });

  /* ---- Filter popovers close on outside click / Escape ------------------- */
  /* <details> has no light-dismiss of its own, so once the panel floats over the page
     it would stay open until clicked again — three open panels stacking on top of each
     other. */
  /* pointerdown, not click: a slider drag that starts on a handle inside the panel and
     releases outside it fires click with the target outside, which closed the panel
     mid-drag. Where the press LANDS is what decides intent. */
  document.addEventListener('pointerdown', function (e) {
    document.querySelectorAll('.collection-filter[open]').forEach(function (d) {
      if (!d.contains(e.target)) d.removeAttribute('open');
    });
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('.collection-filter[open]').forEach(function (d) {
      d.removeAttribute('open');
      var summary = d.querySelector('summary');
      if (summary && d.contains(document.activeElement)) summary.focus();
    });
  });

  /* ---- Dismissible announcement bar ------------------------------------- */
  document.querySelectorAll('[data-announcement-close]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var bar = btn.closest('[data-announcement]');
      if (bar) bar.hidden = true;
      try {
        var key = bar && bar.getAttribute('data-announcement');
        if (key) sessionStorage.setItem('fs-announce-' + key, 'closed');
      } catch (e) {}
    });
  });
  document.querySelectorAll('[data-announcement]').forEach(function (bar) {
    try {
      var key = bar.getAttribute('data-announcement');
      if (key && sessionStorage.getItem('fs-announce-' + key) === 'closed') bar.hidden = true;
    } catch (e) {}
  });

  /* ---- Page table of contents (long policy pages) -----------------------
     Built from the page's own <h2>s. The nav ships hidden and is only revealed
     when there are at least two headings, so a short page never shows an empty
     or pointless box — and with JS off it stays hidden rather than broken. */
  (function () {
    var nav = document.querySelector('[data-page-toc]');
    var list = document.querySelector('[data-page-toc-list]');
    var content = document.querySelector('[data-page-content]');
    if (!nav || !list || !content) return;

    var headings = [].slice.call(content.querySelectorAll('h2'));
    if (headings.length < 2) return;

    headings.forEach(function (h, i) {
      if (!h.id) {
        var slug = (h.textContent || '')
          .toLowerCase().trim()
          .replace(/[^\wåäö\s-]/g, '')
          .replace(/\s+/g, '-')
          .slice(0, 60);
        h.id = 'sec-' + (slug || 'avsnitt') + '-' + i;
      }
      var li = document.createElement('li');
      var a = document.createElement('a');
      a.href = '#' + h.id;
      a.textContent = h.textContent;
      li.appendChild(a);
      list.appendChild(li);
    });

    nav.hidden = false;
  })();

  /* ---- Click-to-load embeds (no heavy third-party autoload) -------------- */
  document.querySelectorAll('[data-embed-load]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var wrap = btn.closest('[data-embed]');
      var src = wrap && wrap.getAttribute('data-embed-src');
      if (!wrap || !src) return;
      var iframe = document.createElement('iframe');
      iframe.src = src;
      iframe.loading = 'lazy';
      iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
      iframe.setAttribute('allowfullscreen', '');
      iframe.title = wrap.getAttribute('data-embed-title') || 'Media';
      wrap.innerHTML = '';
      wrap.appendChild(iframe);
    });
  });

  /* ---- Details-based disclosures close on Escape ------------------------ */
  /* Close directly rather than .click()ing every open toggle — with two synced
     toggles that would fire twice and re-open the menu. */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('[data-nav-toggle][aria-expanded="true"]').forEach(function (btn) {
      var targetId = btn.getAttribute('aria-controls');
      var panel = targetId && document.getElementById(targetId);
      if (panel && !panel.hidden) setNavOpen(panel, targetId, false);
    });
  });
})();
